import { useCallback, useMemo } from 'react';
import type { Nullable } from 'corgi-types';
import isEqual from 'lodash-es/isEqual';

import type { GroupValue as GroupValueItem } from '../../../../product-mvc-buttons/components/types';
import type { ChangeHandler, SelectedValue } from '../types';

interface UseGroupValueOptions {
  articleSku: string;
  articleUrl: string;
  variationKey: Nullable<string>;
  groupValue: Pick<
    GroupValueItem,
    'sku' | 'url' | 'title' | 'key' | 'disabled' | 'hasAlternatives'
  >;
  selectedValue: Nullable<SelectedValue>;
  listLength: number;
  onChange: ChangeHandler;
}

const isValueSelected = <T extends SelectedValue>(current: T, selected: Nullable<T>) => {
  if (!selected) {
    return false;
  }

  return isEqual(current, selected);
};

const getValueInput = ({
  articleSku,
  articleUrl,
  groupValue,
  variationKey,
  listLength,
}: Omit<UseGroupValueOptions, 'selectedValue' | 'onChange'>): SelectedValue => {
  const { key: groupValueKey, title, disabled, hasAlternatives } = groupValue;
  const isTrap = disabled && !!hasAlternatives;
  const sku = groupValue.sku ?? articleSku;
  const url = groupValue.url ?? articleUrl;

  if (isTrap) {
    return {
      title,
      sku,
      alternativeVariantKey: variationKey ?? '',
      alternativeVariantValue: groupValueKey,
      listLength,
    };
  }

  return { title, url, variantKey: groupValue.key };
};

export const useGroupValue = ({ onChange, selectedValue, ...options }: UseGroupValueOptions) => {
  const value = useMemo(() => getValueInput(options), [options]);
  const handleChange = useCallback(
    () => onChange({ value, title: options.groupValue.title }),
    [options]
  );
  const isSelected = isValueSelected(value, selectedValue);

  return { value, isSelected, handleChange };
};
